import { inject, Injectable, signal, Signal } from '@angular/core';

// eslint-disable-next-line boundaries/external
import localforage from 'localforage';
import { from, Observable } from 'rxjs';

import { LocalStorageAccessor, LocalStorageKeyBuilder } from './local-storage.service';

class DefaultKeyBuilder implements LocalStorageKeyBuilder {
  public buildScopedKey(key: string): string {
    return `tellent_${key}`;
  }
}

type CompatPromise<T> = Promise<T> & {
  toSignal: (initialValue?: T) => Signal<T | undefined>;
  toObservable: () => Observable<T>;
};

export const promiseCompatProxy = <T = unknown>(promise: Promise<T>) => {
  const proxy = new Proxy(promise, {
    get(target, key) {
      switch (key) {
        case 'toSignal':
          return function (initialValue: T) {
            const sig = signal<T | undefined>(initialValue ?? undefined);

            promise.then(value => sig.set(value));

            return sig;
          };

        case 'toObservable':
          return function () {
            return from(promise);
          };

        default:
          // @ts-ignore
          return target[key];
      }
    },
  });

  return <CompatPromise<T>>proxy;
};

@Injectable({ providedIn: 'root' })
export class IndexedDbService implements LocalStorageAccessor {
  #keyBuilder = inject(LocalStorageKeyBuilder, { optional: true }) ?? new DefaultKeyBuilder();
  #storage = localforage.createInstance({
    name: this.#keyBuilder.buildScopedKey('tellent_db'),
  });

  public get<T = unknown>(key: string, defaultValue: T | null = null): CompatPromise<T | null> {
    return promiseCompatProxy<T | null>(
      this.#storage.getItem(key).then(value => <T>value ?? defaultValue ?? null),
    );
  }

  public set<T = unknown>(key: string, value: T): CompatPromise<T | null> {
    return promiseCompatProxy<T | null>(this.#storage.setItem(key, value));
  }

  public has(key: string): CompatPromise<boolean | null> {
    return promiseCompatProxy<boolean | null>(
      this.#storage.keys().then(keys => keys.includes(key)),
    );
  }

  public remove(key: string): Promise<void> {
    return this.#storage.removeItem(key);
  }

  // for testing purposes, do not use directly - it clears the entire idb
  private clear() {
    return this.#storage.clear();
  }
}
